<template>
  <div class="snabzhents-page">

    <!-- ===== ШАПКА ===== -->
    <div class="page-top">
      <div class="page-top-left">
        <h2 class="page-title">Заявки снабженца</h2>
        <div v-if="!isFetching && items.length" class="summary-stats">
          <span class="stat-item">{{ items.length }} поз.</span>
          <span class="stat-item stat-item--success">✓ {{ countByStatus(['confirmed', 'shipped']) }}</span>
          <span class="stat-item stat-item--warning">⏳ {{ countByStatus(['new', 'sent']) }}</span>
          <span class="stat-item stat-item--danger">✗ {{ countByStatus(['rejected', 'partial']) }}</span>
        </div>
      </div>
      <!-- SBS-проект в шапке -->
      <div v-if="isShowAll || filters.sbs_project_id" class="page-top-project">
        <div class="filter-with-clear">
          <div class="filter-input-wrapper" @click="openSbsProjectModal">
            <q-input
              :model-value="selectedSbsProjectLabel"
              outlined dense readonly
              placeholder="SBS-проект"
              style="min-width: 200px;"
            >
              <template v-slot:prepend>
                <q-icon name="business" color="primary" size="16px" />
              </template>
            </q-input>
          </div>
          <q-btn
            v-if="filters.sbs_project_id"
            flat round dense icon="close" size="xs" color="grey-6"
            @click="clearSbsProject"
          />
        </div>
      </div>
    </div>

    <!-- ===== ПАНЕЛЬ ФИЛЬТРОВ + ВКЛАДКИ ===== -->
    <div class="toolbar">

      <!-- Период -->
      <div class="toolbar-period">
        <div class="period-seg">
          <button
            v-for="opt in periodOptions"
            :key="opt.value"
            class="period-seg-btn"
            :class="{ active: periodMode === opt.value }"
            @click="setPeriodMode(opt.value)"
          >{{ opt.label }}</button>
        </div>

        <!-- Месяцы -->
        <div v-if="periodMode === 'month'" class="month-chips">
          <button
            v-for="m in availableMonths"
            :key="m.value"
            class="chip"
            :class="{ 'chip--active': selectedMonth === m.value }"
            @click="selectMonth(m.value)"
          >{{ m.label }}</button>
        </div>

        <!-- Произвольный -->
        <div v-if="periodMode === 'range'" class="range-inputs">
          <CustomDate
            :value="filters.date_shipping_begin"
            placeholder="с"
            :width="120"
            @change_date="(v) => onDateRangeChange('date_shipping_begin', v)"
          />
          <span class="range-sep">—</span>
          <CustomDate
            :value="filters.date_shipping_end"
            placeholder="по"
            :width="120"
            @change_date="(v) => onDateRangeChange('date_shipping_end', v)"
          />
          <span v-if="rangeError" class="range-error">{{ rangeError }}</span>
        </div>
      </div>

      <div class="toolbar-divider"></div>

      <!-- Тип потребности -->
      <div class="type-chips">
        <button
          class="chip"
          :class="{ 'chip--active': filters.required_type_id === null }"
          @click="selectRequiredType(null)"
        >Все</button>
        <button
          v-for="rt in filteredRequiredTypes"
          :key="rt.required_type_id"
          class="chip"
          :class="{ 'chip--active': filters.required_type_id === rt.required_type_id }"
          @click="selectRequiredType(rt.required_type_id)"
        >{{ rt.required_type_name }}</button>
      </div>

      <div class="toolbar-spacer"></div>

      <!-- Вкладки видов -->
      <div class="view-tabs">
        <button
          v-for="opt in viewOptions"
          :key="opt.value"
          class="view-tab"
          :class="{ 'view-tab--active': currentView === opt.value }"
          @click="currentView = opt.value"
        >{{ opt.label }}</button>
      </div>
    </div>

    <!-- Загрузка -->
    <div v-if="isFetching" class="loading-state">
      <q-spinner-dots color="primary" size="40px" />
      <div class="loading-text">Загрузка...</div>
    </div>

    <!-- Нет данных -->
    <div v-else-if="!items.length" class="empty-state">
      <q-icon name="inventory_2" size="48px" color="grey-4" />
      <div class="empty-text">Нет данных для выбранного периода и фильтров</div>
    </div>

    <!-- === ВИД: ШАХМАТКА === -->
    <div v-else-if="currentView === 'matrix'" class="matrix-view">
      <!-- Навигация по датам -->
      <div class="matrix-nav">
        <q-btn
          flat round dense icon="chevron_left"
          :disable="!matrixCanPrev"
          @click="matrixPrev"
          class="matrix-nav-btn"
        />
        <span class="matrix-nav-label">{{ matrixPageLabel }}</span>
        <q-btn
          flat round dense icon="chevron_right"
          :disable="!matrixCanNext"
          @click="matrixNext"
          class="matrix-nav-btn"
        />
        <span class="matrix-nav-total">{{ matrixDates.length }} дат всего</span>
      </div>

      <div class="matrix-scroll-wrapper">
        <table class="matrix-table">
          <thead>
            <tr>
              <th class="matrix-th matrix-th--material">Материал</th>
              <th
                v-for="date in matrixVisibleDates"
                :key="date"
                class="matrix-th matrix-th--date"
                :class="{ 'matrix-th--today': formatDateCalendar(date).isToday }"
              >
                <div class="matrix-date-header">
                  <div class="matrix-date-dow">{{ formatDateCalendar(date).dow }}</div>
                  <div class="matrix-date-day">{{ formatDateCalendar(date).day }}</div>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="typeGroup in matrixTypeGroups" :key="'type-' + typeGroup.required_type_id">
              <!-- Строка-заголовок типа потребности (только если тип не выбран) -->
              <tr v-if="!filters.required_type_id" class="matrix-type-header-row">
                <td :colspan="matrixVisibleDates.length + 1" class="matrix-type-header-td">
                  {{ typeGroup.required_type_name || 'Без типа' }}
                </td>
              </tr>
              <!-- Строки материалов -->
              <tr
                v-for="row in typeGroup.materials"
                :key="row.material_id"
                :class="['matrix-row', getRowClass(row)]"
              >
                <!-- Левая ячейка: только материал -->
                <td class="matrix-td matrix-td--material">
                  <div class="material-cell">
                    <div class="material-name">{{ row.material_name }}</div>
                    <div v-if="filters.required_type_id" class="material-meta">{{ row.required_type_name }}</div>
                  </div>
                </td>
                <!-- Ячейки по датам -->
                <td
                  v-for="date in matrixVisibleDates"
                  :key="date"
                  class="matrix-td matrix-td--cell"
                  :class="{ 'matrix-td--today': formatDateCalendar(date).isToday }"
                >
                  <div
                    v-for="offer in row.byDate[date] || []"
                    :key="offer._key"
                    class="offer-card"
                    :class="getOfferCardClass(offer)"
                  >
                    <div class="offer-header">
                      <span class="offer-provider">{{ offer.provider_name }}</span>
                      <span class="offer-price">{{ formatMoney(offer.price) }}</span>
                    </div>
                    <div class="offer-qty">{{ formatNumber(offer.material_cnt) }}</div>
                    <div v-if="isShowAll && offer.project_name" class="offer-project">
                      {{ offer.resident_name || offer.project_name }}
                    </div>
                    <div class="offer-footer">
                      <q-badge :color="getStatusColor(offer.provider_request_status_id)" class="offer-badge">
                        {{ offer.provider_request_status_name || '—' }}
                      </q-badge>
                    </div>
                    <div v-if="offer._invoices && offer._invoices.length" class="offer-invoices">
                      <q-btn
                        v-for="inv in offer._invoices"
                        :key="inv.invoice_id"
                        flat dense round size="xs" icon="receipt" color="teal"
                        @click.stop="downloadInvoice(inv)"
                      >
                        <q-tooltip>
                          <div>Скачать накладную</div>
                          <div v-if="inv.num">№{{ inv.num }}<span v-if="inv.date"> от {{ inv.date }}</span></div>
                        </q-tooltip>
                      </q-btn>
                    </div>
                    <!-- Тултип с полной информацией -->
                    <q-tooltip max-width="320px" :delay="300">
                      <div class="offer-tooltip">
                        <div><strong>Материал:</strong> {{ offer.material_name }}</div>
                        <div v-if="offer.project_name || offer.resident_name">
                          <strong>Проект:</strong> {{ offer.resident_name || offer.project_name }}
                          <span v-if="offer.project_id" class="tooltip-id">(ID: {{ offer.project_id }})</span>
                        </div>
                        <div v-if="offer._request_ids && offer._request_ids.length">
                          <strong>ID заявки:</strong> {{ offer._request_ids.join(', ') }}
                        </div>
                        <div><strong>Поставщик:</strong> {{ offer.provider_name }}</div>
                        <div><strong>Дата отгрузки:</strong> {{ formatDateShort(offer.date_shipping) }}</div>
                        <div><strong>Кол-во:</strong> {{ formatNumber(offer.material_cnt) }}</div>
                        <div><strong>Цена:</strong> {{ formatMoney(offer.price) }}</div>
                        <div><strong>Сумма:</strong> {{ formatMoney(offer.item_sum) }}</div>
                        <div><strong>Статус:</strong> {{ offer.provider_request_status_name || '—' }}</div>
                        <div v-if="offer._grouped_count > 1"><strong>Объединено позиций:</strong> {{ offer._grouped_count }}</div>
                        <div v-if="offer.has_invoice" class="tooltip-invoice">
                          <q-icon name="receipt" color="teal" size="14px" /> Накладная загружена
                        </div>
                      </div>
                    </q-tooltip>
                  </div>
                  <span v-if="!(row.byDate[date] || []).length" class="matrix-empty">—</span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- === ВИД: ПО ДАТАМ === -->
    <div v-else-if="currentView === 'dates'" class="dates-view">
      <div
        v-for="dateGroup in dateGroups"
        :key="dateGroup.date"
        class="date-group"
      >
        <div class="group-header-content date-group-header">
          <span class="group-date-label">{{ formatDateLong(dateGroup.date) }}</span>
          <div class="group-stats">
            <q-badge color="grey-6">{{ dateGroup.items.length }} поз.</q-badge>
            <q-badge color="blue-grey-6">{{ formatMoney(dateGroup.totalSum) }}</q-badge>
            <q-badge v-if="dateGroup.hasProblem" color="red">Проблема</q-badge>
            <q-badge v-else-if="dateGroup.allGood" color="green">Подтверждено</q-badge>
          </div>
        </div>

        <!-- Внутри группы по датам — по типу потребности -->
        <div
          v-for="typeGroup in dateGroup.byType"
          :key="typeGroup.required_type_name"
          class="type-group"
        >
          <div class="type-group-header">{{ typeGroup.required_type_name || 'Без типа' }}</div>
          <q-table
            :rows="typeGroup.items"
            :columns="datesTableColumns"
            row-key="_key"
            flat
            bordered
            dense
            hide-bottom
            :rows-per-page-options="[0]"
            class="items-table items-table--fixed"
          >
            <template v-slot:body-cell-material_name="props">
              <q-td :props="props" class="material-name-td">
                <div class="mat-name">{{ props.row.material_name }}</div>
                <div v-if="isShowAll && (props.row.resident_name || props.row.project_name)" class="mat-project">
                  {{ props.row.resident_name || props.row.project_name }}
                </div>
              </q-td>
            </template>
            <template v-slot:body-cell-provider_name="props">
              <q-td :props="props">
                <span class="provider-value--provider">{{ props.row.provider_name }}</span>
              </q-td>
            </template>
            <template v-slot:body-cell-item_sum="props">
              <q-td :props="props" class="text-right">
                <span class="sum-value">{{ formatMoney(props.row.item_sum) }}</span>
              </q-td>
            </template>
            <template v-slot:body-cell-status_invoice="props">
              <q-td :props="props" class="status-invoice-td">
                <q-badge :color="getStatusColor(props.row.provider_request_status_id)" class="status-badge">
                  {{ props.row.provider_request_status_name || '—' }}
                </q-badge>
                <div v-if="props.row._invoices && props.row._invoices.length" class="invoice-btns">
                  <q-btn
                    v-for="inv in props.row._invoices"
                    :key="inv.invoice_id"
                    flat dense round size="xs" icon="receipt" color="teal"
                    @click="downloadInvoice(inv)"
                  >
                    <q-tooltip>
                      <div>Скачать накладную</div>
                      <div v-if="inv.num">№{{ inv.num }}<span v-if="inv.date"> от {{ inv.date }}</span></div>
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-td>
            </template>
          </q-table>
        </div>
      </div>
    </div>

    <!-- === ВИД: ПО ПОСТАВЩИКАМ === -->
    <div v-else-if="currentView === 'suppliers'" class="suppliers-view">
      <div
        v-for="supGroup in supplierGroups"
        :key="supGroup.provider_id"
        class="supplier-group"
      >
        <div class="group-header-content supplier-group-header" :class="{ 'supplier-group-header--problem': supGroup.hasProblem }">
          <span class="supplier-name">{{ supGroup.provider_name }}</span>
          <div class="group-stats">
            <q-badge color="grey-6">{{ supGroup.items.length }} поз.</q-badge>
            <q-badge color="blue-grey-6">{{ formatMoney(supGroup.totalSum) }}</q-badge>
            <q-badge v-if="supGroup.hasProblem" color="red">Проблема</q-badge>
            <q-badge v-else-if="supGroup.allGood" color="green">Подтверждено</q-badge>
          </div>
        </div>
        <q-table
          :rows="supGroup.items"
          :columns="suppliersTableColumns"
          row-key="_key"
          flat
          bordered
          dense
          hide-bottom
          :rows-per-page-options="[0]"
          class="items-table items-table--fixed"
        >
          <template v-slot:body-cell-material_name="props">
            <q-td :props="props" class="material-name-td">
              <div class="mat-name">{{ props.row.material_name }}</div>
              <div v-if="props.row.required_type_name" class="mat-type">{{ props.row.required_type_name }}</div>
              <div v-if="isShowAll && (props.row.resident_name || props.row.project_name)" class="mat-project">
                {{ props.row.resident_name || props.row.project_name }}
              </div>
            </q-td>
          </template>
          <template v-slot:body-cell-status_invoice="props">
            <q-td :props="props" class="status-invoice-td">
              <q-badge :color="getStatusColor(props.row.provider_request_status_id)" class="status-badge">
                {{ props.row.provider_request_status_name || '—' }}
              </q-badge>
              <div v-if="props.row._invoices && props.row._invoices.length" class="invoice-btns">
                <q-btn
                  v-for="inv in props.row._invoices"
                  :key="inv.invoice_id"
                  flat dense round size="xs" icon="receipt" color="teal"
                  @click="downloadInvoice(inv)"
                >
                  <q-tooltip>
                    <div>Скачать накладную</div>
                    <div v-if="inv.num">№{{ inv.num }}<span v-if="inv.date"> от {{ inv.date }}</span></div>
                  </q-tooltip>
                </q-btn>
              </div>
            </q-td>
          </template>
        </q-table>
      </div>
    </div>

    <!-- === Диалог выбора SBS-проекта === -->
    <q-dialog v-model="sbsProjectDialog.show" persistent>
      <q-card style="min-width: 700px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор SBS-проекта</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="sbsProjectDialog.show = false" />
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="sbsProjectSearch"
            outlined dense
            placeholder="Поиск по названию или коду..."
            clearable autofocus
            @update:model-value="onSbsSearchInput"
          >
            <template v-slot:prepend><q-icon name="search" /></template>
          </q-input>
          <div style="margin-top: 16px;">
            <q-linear-progress v-if="isLoadingSbsProjects" indeterminate />
            <div v-else-if="sbsProjectList.length === 0" style="text-align:center;padding:40px;color:#999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top:12px;">Проекты не найдены</div>
            </div>
            <template v-else>
              <q-table
                :rows="sbsProjectList"
                :columns="[
                  { name: 'sbs_project_id', label: 'ID', field: 'sbs_project_id', align: 'center', style: 'width: 80px' },
                  { name: 'naimenovanie', label: 'Наименование', field: 'naimenovanie', align: 'left' },
                  { name: 'kod', label: 'Код', field: 'kod', align: 'left', style: 'width: 120px' },
                  { name: 'actions', label: '', field: 'actions', align: 'center', style: 'width: 100px' },
                ]"
                row-key="sbs_project_id"
                flat bordered hide-bottom
                :rows-per-page-options="[0]"
                style="max-height: 400px; overflow-y: auto;"
              >
                <template v-slot:body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn label="Выбрать" color="primary" size="sm" unelevated @click="selectSbsProject(props.row)" />
                  </q-td>
                </template>
              </q-table>
              <div style="font-size:13px;color:#666;margin-top:8px;padding:0 4px;">
                Показано {{ sbsProjectList.length }}
                <span v-if="sbsProjectTotalCount > sbsProjectList.length"> из {{ sbsProjectTotalCount }} — уточните поиск</span>
              </div>
            </template>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="sbsProjectDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import CustomDate from "@/components/form/CustomDate";
import { getSnabzhentsList, getSbsProjects, getRequiredTypes } from "./services/index.js";
import { downloadInvoice as downloadInvoiceApi } from "@/views/SupplierRequests/services";

const STATUS_COLORS = {
  100:  'grey',
  200:  'blue',
  250:  'teal',
  300:  'orange',
  400:  'red',
  500:  'red-8',
  600:  'green',
  700:  'light-green',
  800:  'deep-orange',
  1500: 'cyan',
};

function getMonthsRange() {
  const months = [];
  const now = new Date();
  for (let i = -1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
}

/**
 * Парсит invoices_json поле (строка или массив) в массив { invoice_id, aws_url }.
 */
function parseInvoices(raw) {
  if (!raw) return [];
  try {
    const arr = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Объединяет строки с одинаковым material_id + project_id + date_shipping + provider_id.
 * Суммирует material_cnt и item_sum, собирает все уникальные накладные.
 */
function groupItems(items) {
  const map = new Map();
  for (const item of items) {
    const key = `${item.material_id}|${item.project_id || ''}|${item.date_shipping || ''}|${item.provider_id || ''}`;
    const itemInvoices = parseInvoices(item.invoices_json);
    if (!map.has(key)) {
      map.set(key, {
        ...item,
        _key: key,
        _grouped_count: 1,
        material_cnt: item.material_cnt || 0,
        item_sum: item.item_sum || 0,
        has_invoice: itemInvoices.length > 0,
        _invoices: itemInvoices,
        _request_ids: item.provider_request_id ? [item.provider_request_id] : [],
      });
    } else {
      const g = map.get(key);
      g.material_cnt = (g.material_cnt || 0) + (item.material_cnt || 0);
      g.item_sum = (g.item_sum || 0) + (item.item_sum || 0);
      g._grouped_count++;
      if (item.provider_request_id && !g._request_ids.includes(item.provider_request_id)) {
        g._request_ids.push(item.provider_request_id);
      }
      // Добавляем уникальные накладные
      for (const inv of itemInvoices) {
        if (!g._invoices.some(e => e.invoice_id === inv.invoice_id)) {
          g._invoices.push(inv);
        }
      }
      g.has_invoice = g._invoices.length > 0;
    }
  }
  return [...map.values()];
}

export default {
  name: 'SupplierRequestsFromRequired',
  components: { CustomDate },

  props: {
    initialProjectId: { type: Number, default: null },
    embedded: { type: Boolean, default: false },
  },

  data() {
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return {
      isFetching: false,
      items: [],
      isShowAll: false,

      // Фильтры
      periodMode: 'month',
      selectedMonth: currentMonth,
      rangeError: '',
      filters: {
        date_shipping_begin: null,
        date_shipping_end: null,
        sbs_project_id: null,
        required_type_id: null,
      },

      // Вид
      currentView: 'matrix',
      viewOptions: [
        { label: 'Шахматка', value: 'matrix' },
        { label: 'По датам', value: 'dates' },
        { label: 'По поставщикам', value: 'suppliers' },
      ],

      periodOptions: [
        { label: 'Сегодня', value: 'today' },
        { label: 'По месяцу', value: 'month' },
        { label: 'Произвольный', value: 'range' },
      ],

      // Месяцы
      availableMonths: getMonthsRange(),

      // Типы потребности
      requiredTypes: [],

      // Шахматка — навигация по датам
      matrixPage: 0,
      matrixPageSize: 5,

      // SBS-проект
      selectedSbsProject: null,
      sbsProjectDialog: { show: false },
      sbsProjectList: [],
      sbsProjectSearch: '',
      sbsProjectPage: 1,
      sbsProjectLimit: 20,
      sbsProjectTotalCount: 0,
      isLoadingSbsProjects: false,
      sbsSearchDebounce: null,
    };
  },

  computed: {
    filteredRequiredTypes() {
      const EXCLUDED = ['есть тип потребности', 'нет типа потребности'];
      return this.requiredTypes.filter(
        rt => !EXCLUDED.includes((rt.required_type_name || '').toLowerCase())
      );
    },

    selectedSbsProjectLabel() {
      if (this.selectedSbsProject) {
        return this.selectedSbsProject.naimenovanie || `SBS #${this.selectedSbsProject.sbs_project_id}`;
      }
      return '';
    },

    // Сгруппированные элементы (дедупликация по material+project+date+provider)
    groupedItems() {
      return groupItems(this.items);
    },

    // === Шахматка ===
    matrixDates() {
      const dates = [...new Set(this.items.map(i => i.date_shipping).filter(Boolean))];
      return dates.sort();
    },

    matrixVisibleDates() {
      return this.matrixDates.slice(this.matrixPage, this.matrixPage + this.matrixPageSize);
    },

    matrixCanPrev() {
      return this.matrixPage > 0;
    },

    matrixCanNext() {
      return this.matrixPage + this.matrixPageSize < this.matrixDates.length;
    },

    matrixPageLabel() {
      const visible = this.matrixVisibleDates;
      if (!visible.length) return '';
      const fmt = (iso) => {
        const d = new Date(iso);
        return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
      };
      if (visible.length === 1) return fmt(visible[0]);
      return `${fmt(visible[0])} — ${fmt(visible[visible.length - 1])}`;
    },

    matrixTypeGroups() {
      // Строим grouped items, но сохраняем оригинальные для группировки по типу+материалу
      const typeMap = new Map();

      for (const item of this.items) {
        const typeId = item.required_type_id || 0;
        const typeName = item.required_type_name || '—';

        if (!typeMap.has(typeId)) {
          typeMap.set(typeId, {
            required_type_id: typeId,
            required_type_name: typeName,
            materialMap: new Map(),
          });
        }
        const typeGroup = typeMap.get(typeId);

        const matId = item.material_id;
        if (!typeGroup.materialMap.has(matId)) {
          typeGroup.materialMap.set(matId, {
            material_id: matId,
            material_name: item.material_name,
            required_type_name: typeName,
            byDate: {},
          });
        }
        const matRow = typeGroup.materialMap.get(matId);
        const date = item.date_shipping;
        if (date) {
          if (!matRow.byDate[date]) matRow.byDate[date] = [];
          matRow.byDate[date].push(item);
        }
      }

      // Дедупликация внутри каждой ячейки (byDate)
      for (const typeGroup of typeMap.values()) {
        for (const matRow of typeGroup.materialMap.values()) {
          for (const date of Object.keys(matRow.byDate)) {
            matRow.byDate[date] = groupItems(matRow.byDate[date]);
          }
        }
      }

      // Сортируем типы, внутри — материалы
      return [...typeMap.values()]
        .sort((a, b) => (a.required_type_name || '').localeCompare(b.required_type_name || ''))
        .map(tg => ({
          ...tg,
          materials: [...tg.materialMap.values()].sort((a, b) =>
            (a.material_name || '').localeCompare(b.material_name || '')
          ),
        }));
    },

    // === По датам ===
    dateGroups() {
      const dateMap = new Map();
      for (const item of this.groupedItems) {
        const date = item.date_shipping || 'unknown';
        if (!dateMap.has(date)) dateMap.set(date, []);
        dateMap.get(date).push(item);
      }
      return [...dateMap.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, items]) => {
          const byTypeMap = new Map();
          for (const item of items) {
            const key = item.required_type_name || 'Без типа';
            if (!byTypeMap.has(key)) byTypeMap.set(key, []);
            byTypeMap.get(key).push(item);
          }
          const statusIds = items.map(i => i.provider_request_status_id);
          return {
            date,
            items,
            totalSum: items.reduce((s, i) => s + (i.item_sum || 0), 0),
            hasProblem: statusIds.some(s => [400, 500, 800].includes(s)),
            allGood: statusIds.every(s => [600, 700].includes(s)),
            byType: [...byTypeMap.entries()].map(([required_type_name, items]) => ({
              required_type_name,
              items,
            })),
          };
        });
    },

    datesTableColumns() {
      return [
        {
          name: 'material_name',
          label: 'Материал',
          field: 'material_name',
          align: 'left',
          style: 'width: 480px',
        },
        {
          name: 'material_cnt',
          label: 'Кол-во',
          field: 'material_cnt',
          align: 'right',
          style: 'width: 80px',
          format: (v) => this.formatNumber(v),
        },
        {
          name: 'provider_name',
          label: 'Поставщик',
          field: 'provider_name',
          align: 'left',
          style: 'width: 180px',
        },
        {
          name: 'price',
          label: 'Цена',
          field: 'price',
          align: 'right',
          style: 'width: 100px',
          format: (v) => this.formatMoney(v),
        },
        {
          name: 'item_sum',
          label: 'Сумма',
          field: 'item_sum',
          align: 'right',
          style: 'width: 120px',
        },
        {
          name: 'status_invoice',
          label: 'Статус / Накладная',
          field: 'provider_request_status_id',
          align: 'center',
          style: 'width: 160px',
        },
      ];
    },

    // === По поставщикам ===
    supplierGroups() {
      const map = new Map();
      for (const item of this.groupedItems) {
        const key = item.provider_id || 0;
        if (!map.has(key)) {
          map.set(key, {
            provider_id: item.provider_id,
            provider_name: item.provider_name || '—',
            items: [],
            totalSum: 0,
            hasProblem: false,
            allGood: true,
          });
        }
        const g = map.get(key);
        g.items.push(item);
        g.totalSum += item.item_sum || 0;
        if ([400, 500, 800].includes(item.provider_request_status_id)) g.hasProblem = true;
        if (![600, 700].includes(item.provider_request_status_id)) g.allGood = false;
      }
      return [...map.values()].sort((a, b) => {
        if (a.hasProblem && !b.hasProblem) return -1;
        if (!a.hasProblem && b.hasProblem) return 1;
        return (a.provider_name || '').localeCompare(b.provider_name || '');
      });
    },

    suppliersTableColumns() {
      return [
        {
          name: 'material_name',
          label: 'Материал',
          field: 'material_name',
          align: 'left',
          style: 'width: 520px',
        },
        {
          name: 'material_cnt',
          label: 'Кол-во',
          field: 'material_cnt',
          align: 'right',
          style: 'width: 80px',
          format: (v) => this.formatNumber(v),
        },
        {
          name: 'date_shipping',
          label: 'Дата отгрузки',
          field: 'date_shipping',
          align: 'center',
          style: 'width: 110px',
          format: (v) => this.formatDateShort(v),
        },
        {
          name: 'price',
          label: 'Цена',
          field: 'price',
          align: 'right',
          style: 'width: 100px',
          format: (v) => this.formatMoney(v),
        },
        {
          name: 'item_sum',
          label: 'Сумма',
          field: 'item_sum',
          align: 'right',
          style: 'width: 120px',
          format: (v) => this.formatMoney(v),
        },
        {
          name: 'status_invoice',
          label: 'Статус / Накладная',
          field: 'provider_request_status_id',
          align: 'center',
          style: 'width: 160px',
        },
      ];
    },
  },

  async mounted() {
    if (this.embedded) {
      this.filters.sbs_project_id = this.initialProjectId ?? null;
    } else {
      this.readFromUrl();
    }
    await this.loadRequiredTypes();
    // При произвольном периоде грузим только если обе даты заполнены
    if (this.periodMode === 'range') {
      if (this.filters.date_shipping_begin && this.filters.date_shipping_end) {
        await this.loadData();
      }
    } else {
      await this.loadData();
    }
  },

  watch: {
    currentView() {
      this.syncToUrl();
    },
  },

  methods: {
    // === URL sync ===

    syncToUrl() {
      if (this.embedded) return;
      const q = {};
      if (this.currentView && this.currentView !== 'matrix') q.view = this.currentView;
      if (this.periodMode && this.periodMode !== 'month') q.period = this.periodMode;
      if (this.selectedMonth) q.month = this.selectedMonth;
      if (this.filters.date_shipping_begin) q.date_from = this.filters.date_shipping_begin;
      if (this.filters.date_shipping_end)   q.date_to   = this.filters.date_shipping_end;
      if (this.filters.required_type_id != null) q.type = String(this.filters.required_type_id);
      if (this.filters.sbs_project_id != null)   q.project = String(this.filters.sbs_project_id);

      const current = this.$route.query;
      const same = Object.keys(q).length === Object.keys(current).length &&
        Object.keys(q).every(k => String(q[k]) === String(current[k]));
      if (!same) {
        this.$router.replace({ query: q }).catch(() => {});
      }
    },

    readFromUrl() {
      const q = this.$route.query;

      this.currentView  = q.view   || 'matrix';
      this.periodMode   = q.period || 'month';
      this.selectedMonth = q.month  || null;
      this.filters.date_shipping_begin = q.date_from || null;
      this.filters.date_shipping_end   = q.date_to   || null;
      this.filters.required_type_id    = q.type    != null ? Number(q.type)    : null;
      this.filters.sbs_project_id      = q.project != null ? Number(q.project) : null;

      // Если тип month и нет месяца — берём текущий
      if (this.periodMode === 'month' && !this.selectedMonth) {
        const now = new Date();
        this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      }

      // Если в URL есть project_id — показываем блок проекта сразу
      if (this.filters.sbs_project_id) {
        this.selectedSbsProject = { sbs_project_id: this.filters.sbs_project_id, naimenovanie: null };
        this.isShowAll = true;
      }
    },

    // === Загрузка данных ===

    buildParams() {
      const params = {};
      if (this.periodMode === 'month' && this.selectedMonth) {
        const [year, month] = this.selectedMonth.split('-');
        const lastDay = new Date(year, parseInt(month), 0).getDate();
        params.date_shipping_begin = `${this.selectedMonth}-01`;
        params.date_shipping_end   = `${this.selectedMonth}-${String(lastDay).padStart(2, '0')}`;
      } else {
        if (this.filters.date_shipping_begin) params.date_shipping_begin = this.filters.date_shipping_begin;
        if (this.filters.date_shipping_end)   params.date_shipping_end   = this.filters.date_shipping_end;
      }
      if (this.filters.sbs_project_id)   params.sbs_project_id   = this.filters.sbs_project_id;
      if (this.filters.required_type_id) params.required_type_id = this.filters.required_type_id;
      return params;
    },

    async loadData() {
      this.isFetching = true;
      const res = await getSnabzhentsList(this.buildParams());
      this.isFetching = false;
      if (!res) return;
      this.items = res.data || [];
      if (res.is_show_all) {
        this.isShowAll = true;
      } else if (!this.isShowAll) {
        this.isShowAll = false;
      }
      this.initMatrixPage();
    },

    initMatrixPage() {
      const today = new Date().toISOString().slice(0, 10);
      const dates = [...new Set(this.items.map(i => i.date_shipping).filter(Boolean))].sort();
      const idx = dates.indexOf(today);
      if (idx !== -1) {
        // Страница, на которой попадает сегодня
        this.matrixPage = Math.floor(idx / this.matrixPageSize) * this.matrixPageSize;
      } else {
        this.matrixPage = 0;
      }
    },

    async loadRequiredTypes() {
      this.requiredTypes = await getRequiredTypes();
    },

    // === Период ===

    setPeriodMode(mode) {
      this.rangeError = '';
      this.periodMode = mode;
      if (mode === 'today') {
        const today = new Date().toISOString().slice(0, 10);
        this.filters.date_shipping_begin = today;
        this.filters.date_shipping_end   = today;
        this.selectedMonth = null;
        this.syncToUrl();
        this.loadData();
      } else if (mode === 'month') {
        this.filters.date_shipping_begin = null;
        this.filters.date_shipping_end   = null;
        if (!this.selectedMonth) {
          const now = new Date();
          this.selectedMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        }
        this.syncToUrl();
        this.loadData();
      } else {
        // range — сбрасываем, ждём ввода дат
        this.selectedMonth = null;
        this.filters.date_shipping_begin = null;
        this.filters.date_shipping_end   = null;
        this.items = [];
        this.syncToUrl();
      }
    },

    selectMonth(value) {
      this.selectedMonth = value;
      this.syncToUrl();
      this.loadData();
    },

    onDateRangeChange(field, value) {
      this.filters[field] = value;
      this.rangeError = '';

      const begin = this.filters.date_shipping_begin;
      const end   = this.filters.date_shipping_end;

      if (begin && end) {
        const diff = (new Date(end) - new Date(begin)) / 86400000;
        if (diff < 0) {
          this.rangeError = 'Дата "по" не может быть раньше даты "с"';
          return;
        }
        if (diff > 31) {
          this.rangeError = 'Период не может превышать 31 день';
          this.$q.notify({
            type: 'warning',
            message: 'Период не может превышать 31 день',
            position: 'top',
          });
          this.filters[field] = null;
          return;
        }
      }

      // Загружаем только если обе даты заполнены
      if (begin && end) {
        this.syncToUrl();
        this.loadData();
      } else {
        this.syncToUrl();
      }
    },

    // === Шахматка: навигация ===

    matrixPrev() {
      this.matrixPage = Math.max(0, this.matrixPage - this.matrixPageSize);
    },

    matrixNext() {
      this.matrixPage = Math.min(
        this.matrixDates.length - 1,
        this.matrixPage + this.matrixPageSize
      );
    },

    formatDateCalendar(iso) {
      if (!iso) return { dow: '', day: '', isToday: false };
      const d = new Date(iso);
      const today = new Date().toISOString().slice(0, 10);
      return {
        dow: d.toLocaleDateString('ru-RU', { weekday: 'short' }),
        day: d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }),
        isToday: iso === today,
      };
    },

    // === Тип потребности ===

    selectRequiredType(id) {
      this.filters.required_type_id = id;
      this.syncToUrl();
      this.loadData();
    },

    // === SBS-проект ===

    async openSbsProjectModal() {
      this.sbsProjectDialog.show = true;
      this.sbsProjectSearch = '';
      this.sbsProjectPage = 1;
      this.sbsProjectTotalCount = 0;
      this.sbsProjectList = [];
      await this.loadSbsProjects();
    },

    async loadSbsProjects(page) {
      if (page) this.sbsProjectPage = page;
      this.isLoadingSbsProjects = true;
      const data = await getSbsProjects(this.sbsProjectSearch || '', this.sbsProjectPage, this.sbsProjectLimit);
      this.isLoadingSbsProjects = false;
      if (Array.isArray(data) && data.length > 0) {
        this.sbsProjectList = data;
        this.sbsProjectTotalCount = data[0].total_count || data.length;
      } else {
        this.sbsProjectList = [];
        this.sbsProjectTotalCount = 0;
      }
    },

    onSbsSearchInput() {
      if (this.sbsSearchDebounce) clearTimeout(this.sbsSearchDebounce);
      this.sbsSearchDebounce = setTimeout(() => {
        this.sbsProjectPage = 1;
        this.loadSbsProjects();
      }, 400);
    },

    selectSbsProject(row) {
      this.filters.sbs_project_id = row.sbs_project_id;
      this.selectedSbsProject = row;
      this.sbsProjectDialog.show = false;
      this.syncToUrl();
      this.loadData();
    },

    clearSbsProject() {
      this.filters.sbs_project_id = null;
      this.selectedSbsProject = null;
      this.syncToUrl();
      this.loadData();
    },

    // === Накладная ===

    async downloadInvoice(inv) {
      const invoiceId = inv?.invoice_id;
      if (!invoiceId) return;

      try {
        const response = await downloadInvoiceApi(invoiceId);
        const contentType = response?.headers?.['content-type'] || 'application/pdf';
        const disposition = response?.headers?.['content-disposition'];
        let filename = `Накладная_${invoiceId}.pdf`;
        if (disposition) {
          const match = disposition.match(/filename[^;=\n]*=['"]?([^'";\n]+)['"]?/);
          if (match) filename = decodeURIComponent(match[1]);
        }
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$q.notify({
          type: 'negative',
          message: e?.response?.data?.error || 'Ошибка скачивания накладной',
        });
      }
    },

    // === Вспомогательные ===

    countByStatus(statusNames) {
      const statusIds = {
        new:       [100],
        sent:      [200],
        confirmed: [250, 600, 700],
        shipped:   [1500],
        rejected:  [400, 500],
        partial:   [800],
      };
      const ids = statusNames.flatMap(n => statusIds[n] || []);
      return this.items.filter(i => ids.includes(i.provider_request_status_id)).length;
    },

    getRowClass(row) {
      const allItems = Object.values(row.byDate).flat();
      if (!allItems.length) return '';
      const ids = allItems.map(i => i.provider_request_status_id);
      if (ids.some(s => [400, 500, 800].includes(s))) return 'matrix-row--problem';
      if (ids.every(s => [600, 700, 1500, 250].includes(s))) return 'matrix-row--good';
      return '';
    },

    getOfferCardClass(offer) {
      const id = offer.provider_request_status_id;
      if ([400, 500].includes(id)) return 'offer-card--rejected';
      if ([800].includes(id)) return 'offer-card--partial';
      if ([600, 700, 1500].includes(id)) return 'offer-card--confirmed';
      return '';
    },

    getStatusColor(statusId) {
      return STATUS_COLORS[statusId] || 'grey';
    },

    formatMoney(v) {
      if (v == null) return '—';
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);
    },

    formatNumber(v) {
      if (v == null) return '—';
      return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 3 }).format(v);
    },

    formatDateShort(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
    },

    formatDateLong(iso) {
      if (!iso || iso === 'unknown') return 'Без даты';
      const d = new Date(iso);
      return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
    },
  },
};
</script>

<style scoped>
/* === БАЗА === */
.snabzhents-page {
  padding: 8px 16px 16px;
  max-width: 100%;
  font-family: inherit;
  font-size: 13px;
  color: #495057;
}

/* === ШАПКА === */
.page-top {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 8px;
}

.page-top-left {
  display: flex;
  align-items: baseline;
  gap: 16px;
  flex: 1;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #343a40;
  margin: 0;
  white-space: nowrap;
}

.summary-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-item {
  font-size: 12px;
  color: #6c757d;
  white-space: nowrap;
}

.stat-item--success { color: #28a745; font-weight: 600; }
.stat-item--warning { color: #fd7e14; font-weight: 600; }
.stat-item--danger  { color: #dc3545; font-weight: 600; }

.page-top-project {
  display: flex;
  align-items: center;
}

.filter-with-clear {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* === ТУЛБАР (фильтры + вкладки в одной строке) === */
.toolbar {
  display: flex;
  align-items: center;
  gap: 0;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 0 8px;
  margin-bottom: 10px;
  min-height: 40px;
  flex-wrap: wrap;
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 8px;
  flex-shrink: 0;
}

.toolbar-spacer {
  flex: 1;
}

/* Период — сегментированный контрол */
.toolbar-period {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
}

.period-seg {
  display: flex;
  background: #f0f2f5;
  border-radius: 5px;
  padding: 2px;
  gap: 1px;
}

.period-seg-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #6c757d;
  border-radius: 4px;
  transition: all 0.15s;
  white-space: nowrap;
}

.period-seg-btn:hover {
  color: #343a40;
  background: #fff;
}

.period-seg-btn.active {
  background: #fff;
  color: #1565c0;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
}

/* Чипы месяцев */
.month-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Произвольный период */
.range-inputs {
  display: flex;
  align-items: center;
  gap: 6px;
}

.range-sep {
  color: #adb5bd;
  font-size: 12px;
}

.range-error {
  font-size: 11px;
  color: #dc3545;
  white-space: nowrap;
}

/* Универсальный чип */
.chip {
  background: none;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
  padding: 2px 10px;
  font-size: 12px;
  color: #495057;
  transition: all 0.12s;
  white-space: nowrap;
  line-height: 1.8;
}

.chip:hover {
  border-color: #1565c0;
  color: #1565c0;
  background: #f0f4ff;
}

.chip--active {
  background: #1565c0;
  border-color: #1565c0;
  color: #fff;
  font-weight: 500;
}

/* Вкладки видов */
.view-tabs {
  display: flex;
  gap: 0;
  padding: 5px 0;
}

.view-tab {
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #6c757d;
  transition: all 0.15s;
  white-space: nowrap;
}

.view-tab:hover {
  color: #343a40;
  border-bottom-color: #adb5bd;
}

.view-tab--active {
  color: #1565c0;
  border-bottom-color: #1565c0;
  font-weight: 600;
}

/* Тип потребности */
.type-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 5px 0;
}

/* === ЗАГРУЗКА / ПУСТО === */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9e9e9e;
  gap: 12px;
}

.loading-text,
.empty-text {
  font-size: 14px;
}

/* === ШАХМАТКА === */

/* Навигация по датам */
.matrix-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

.matrix-nav-btn {
  color: #495057;
}

.matrix-nav-label {
  font-size: 14px;
  font-weight: 600;
  color: #343a40;
  min-width: 200px;
  text-align: center;
}

.matrix-nav-total {
  font-size: 11px;
  color: #6c757d;
  margin-left: 4px;
}

.matrix-scroll-wrapper {
  overflow-x: auto;
}

.matrix-table {
  border-collapse: collapse;
  width: 100%;
  min-width: 600px;
  font-size: 12px;
}

.matrix-th {
  background: #e9ecef;
  border: 1px solid #ced4da;
  padding: 7px 10px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.matrix-th--material {
  min-width: 180px;
  position: sticky;
  left: 0;
  z-index: 2;
  background: #e9ecef;
}

.matrix-th--date {
  min-width: 150px;
  text-align: center;
}

.matrix-th--today {
  background: #e8f0fe !important;
  border-top: 3px solid #1a73e8 !important;
}

.matrix-td--today {
  background: #f8fbff;
}

.matrix-date-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.matrix-date-dow {
  font-size: 11px;
  font-weight: 400;
  color: #6c757d;
  text-transform: capitalize;
}

.matrix-date-day {
  font-size: 13px;
  font-weight: 700;
  color: #343a40;
}

.matrix-th--today .matrix-date-day {
  color: #1a73e8;
}

.matrix-th--today .matrix-date-dow {
  color: #1a73e8;
}

.matrix-td {
  border: 1px solid #ced4da;
  padding: 5px 7px;
  vertical-align: top;
}

.matrix-td--material {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 1;
}

.matrix-td--cell {
  vertical-align: top;
  min-width: 140px;
}

.matrix-row:hover .matrix-td {
  background: #f8f9fa;
}

.matrix-row:hover .matrix-td--material {
  background: #f8f9fa;
}

.matrix-row--problem .matrix-td--material {
  border-left: 3px solid #dc3545;
}

.matrix-row--good .matrix-td--material {
  border-left: 3px solid #28a745;
}

/* Строка-заголовок типа потребности */
.matrix-type-header-row .matrix-type-header-td {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-top: 2px solid #1976d2;
  border-bottom: 1px solid #90caf9;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #1565c0;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.material-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.material-name {
  font-weight: 600;
  color: #343a40;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}

.material-meta {
  font-size: 11px;
  color: #6c757d;
}

.matrix-empty {
  color: #ced4da;
  display: block;
  text-align: center;
  padding: 4px;
}

/* Карточка оффера */
.offer-card {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 3px;
  padding: 4px 6px;
  margin-bottom: 3px;
  font-size: 11px;
  cursor: default;
  position: relative;
}

.offer-card--confirmed {
  border-color: #b2dfdb;
  background: #f1f8f6;
}

.offer-card--rejected {
  border-color: #f5c6cb;
  background: #fff5f5;
}

.offer-card--partial {
  border-color: #ffeeba;
  background: #fffdf0;
}

.offer-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 2px;
}

.offer-provider {
  font-weight: 600;
  color: #1565c0;
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100px;
}

.offer-price {
  font-weight: 600;
  color: #28a745;
  font-size: 11px;
  white-space: nowrap;
}

.offer-qty {
  color: #6c757d;
  font-size: 11px;
}

.offer-project {
  font-size: 10px;
  color: #6c757d;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.offer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 3px;
  gap: 4px;
}

.offer-invoices {
  display: flex;
  flex-wrap: wrap;
  gap: 1px;
  margin-top: 2px;
}

.offer-badge {
  font-size: 10px;
  padding: 1px 4px;
}

.offer-tooltip {
  font-size: 12px;
  line-height: 1.6;
}

.tooltip-id {
  font-size: 11px;
  opacity: 0.7;
  margin-left: 4px;
}

.tooltip-invoice {
  margin-top: 4px;
  color: #26a69a;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* === ОБЩИЕ СТИЛИ ТАБЛИЦ === */
.items-table {
  border-radius: 0 !important;
}

.status-badge {
  font-size: 11px;
  padding: 2px 6px;
}

.no-data {
  color: #ced4da;
}

.sum-value {
  font-weight: 600;
  color: #495057;
}

/* Таблица с фиксированными ширинами */
:deep(.items-table--fixed table) {
  table-layout: fixed;
  width: 100%;
}

/* Название материала в таблицах — с переносом */
:deep(.items-table .material-name-td) {
  white-space: normal !important;
  word-break: break-word;
  vertical-align: top;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.mat-name {
  font-weight: 500;
  color: #343a40;
  line-height: 1.4;
}

.mat-type {
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
  font-style: italic;
}

.mat-project {
  font-size: 11px;
  color: #1565c0;
  margin-top: 2px;
}

/* Объединённая колонка статус + накладная */
.status-invoice-td {
  vertical-align: middle;
  text-align: center;
}

.invoice-btns {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 4px;
}

/* === ЗАГОЛОВКИ ГРУПП (общий стиль) === */
.group-header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
}

.group-stats {
  display: flex;
  gap: 6px;
  align-items: center;
}

.group-stats .q-badge {
  font-size: 11px;
  padding: 3px 8px;
  font-weight: 500;
}

/* === ПО ДАТАМ === */
.dates-view .date-group {
  margin-bottom: 16px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  overflow: hidden;
}

.date-group-header {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-bottom: 2px solid #1976d2;
}

.group-date-label {
  font-size: 14px;
  font-weight: 700;
  color: #1565c0;
  letter-spacing: 0.3px;
}

.type-group {
  padding: 0;
}

.type-group-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 4px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.2px;
  text-transform: uppercase;
}

/* === ПО ПОСТАВЩИКАМ === */
.suppliers-view .supplier-group {
  margin-bottom: 16px;
  border: 1px solid #dee2e6;
  border-radius: 5px;
  overflow: hidden;
}

.supplier-group-header {
  background: #f3e5f5;
  border-bottom: 2px solid #7b1fa2;
}

.supplier-group-header--problem {
  background: #ffebee;
  border-bottom-color: #c62828;
}

.supplier-name {
  font-size: 14px;
  font-weight: 700;
  color: #4a148c;
}

.supplier-group-header--problem .supplier-name {
  color: #b71c1c;
}

/* Поставщик в таблице дат */
.provider-value {
  color: #495057;
  font-size: 12px;
}

.provider-value--provider {
  color: #1565c0;
  font-weight: 600;
}
</style>
